// pages/SuperAdmin/SubscriptionManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Package,
  AlertCircle,
  RefreshCw,
  Search,
  ArrowLeft,
  BarChart3,
  Plus,
  X,
  ChevronDown,
  Check
} from 'lucide-react';
import api from '../api';
import { useToast } from './components/ToastContainer';

// Import Components
import DashboardStats from './components/DashboardStats';
import ConfirmDialog from './components/ConfirmDialog';
import PlanCard from './components/PlanCard';
import CreatePlanModal from './components/CreatePlanModal';
import EditPlanModal from './components/EditPlanModal';
import AssignPlanModal from './components/AssignPlanModal';
import ManageTenantSubscriptionModal from './components/ManageTenantSubscriptionModal';
import SubscriptionTable from './components/SubscriptionTable';
import AssignTenantsToPlanModal from './components/AssignTenantsToPlanModal';
import SubscriptionHistoryModal from './components/SubscriptionHistoryModal';
import SetSpecialPriceModal from './components/SetSpecialPriceModal';
import StartTrialModal from './components/StartTrialModal';

// Import Constants
import { MODULE_CATEGORIES, ROLES_LIST } from './constants/subscriptionConstants';

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('plans');
  
  // Confirm Dialog
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    type: 'warning',
    onConfirm: null
  });
  
  // Data
  const [plans, setPlans] = useState([]);
  const [tenantSubscriptions, setTenantSubscriptions] = useState([]);
  const [stats, setStats] = useState({
    totalPlans: 0,
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    trialSubscriptions: 0,
    expiredSubscriptions: 0,
    monthlyRevenue: 0,
    conversionRate: 0,
    planDistribution: []
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');

  // Modals
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [showAssignPlan, setShowAssignPlan] = useState(false);
  const [showEditPlan, setShowEditPlan] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showAssignTenantsModal, setShowAssignTenantsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showSpecialPriceModal, setShowSpecialPriceModal] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  
  // Selected items
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [historyTenant, setHistoryTenant] = useState(null);
  const [selectedTenantForAction, setSelectedTenantForAction] = useState(null);

  const adminId = localStorage.getItem('userId') || localStorage.getItem('adminId') || '';

  // ============================================
  // FORM STATES
  // ============================================
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    type: 'paid',
    annualPrice: '',
    taxName: 'GST',
    taxPercentage: '',
    isTaxInclusive: false,
    maxStudents: '',
    maxStaff: '',
    maxBranches: '1',
    maxAdmins: '1',
    maxStorageGB: '5',
    isActive: true,
    isPublic: true,
    trialEnabled: false,
    trialDays: '14'
  });

  const [assignForm, setAssignForm] = useState({
    tenantId: '',
    planId: '',
    status: 'active',
    billingCycle: 'yearly',
    isTrial: false,
    trialDays: '14',
    specialPrice: ''
  });

  // ============================================
  // DATA LOADING
  // ============================================
  const loadData = useCallback(async (isSilentRefresh = false) => {
    if (!isSilentRefresh) setLoading(true);
    setError(null);
    
    try {
      const [plansRes, tenantsRes, statsRes] = await Promise.all([
        api.get('/api/super-admin/subscription/plans?includeLimits=true'),
        api.get('/api/super-admin/subscription/tenants'),
        api.get('/api/super-admin/subscription/dashboard')
      ]);

      if (plansRes.data.success) {
        const plansData = plansRes.data.data || [];
        
        const processedPlans = plansData.map(plan => ({
          ...plan,
          limits: plan.limits || null,
          moduleCount: plan.summary?.moduleCount || Object.values(plan.modules || {}).filter(v => v).length || 0,
          roleCount: plan.summary?.roleCount || Object.values(plan.roleAccess || {}).filter(v => v).length || 0,
          modules: plan.limits?.modules || plan.modules || {},
          roleAccess: plan.limits?.roleAccess || plan.roleAccess || {},
          hasModule: (moduleName) => {
            if (plan.limits?.modules) {
              return plan.limits.modules[moduleName] === true;
            }
            return plan.modules?.[moduleName] === true;
          },
          hasRole: (roleName) => {
            if (plan.limits?.roleAccess) {
              return plan.limits.roleAccess[roleName] === true;
            }
            return plan.roleAccess?.[roleName] === true;
          }
        }));
        
        setPlans(processedPlans);
        console.log('📊 [SubscriptionManagement] Loaded plans with limits:', processedPlans.length);
      }

      if (tenantsRes.data.success) {
        setTenantSubscriptions(tenantsRes.data.data || []);
      }

      if (statsRes.data.success) {
        const data = statsRes.data.data;
        setStats({
          totalPlans: data.overview?.totalPlans || 0,
          totalSubscriptions: data.overview?.totalSubscriptions || 0,
          activeSubscriptions: data.overview?.activeSubscriptions || 0,
          trialSubscriptions: data.overview?.trialSubscriptions || 0,
          expiredSubscriptions: data.overview?.expiredSubscriptions || 0,
          monthlyRevenue: data.overview?.monthlyRevenue || 0,
          conversionRate: data.overview?.conversionRate || 0,
          planDistribution: data.planDistribution || []
        });
      }
    } catch (err) {
      console.error('❌ Error loading subscription data:', err);
      const msg = err.response?.data?.message || 'Failed to load subscription data';
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // CONFIRM DIALOG
  // ============================================
  const showConfirm = (title, message, onConfirm, confirmText = 'Confirm', type = 'warning') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      confirmText,
      type,
      onConfirm: () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        onConfirm();
      }
    });
  };

  // ============================================
  // REFRESH
  // ============================================
  const refreshData = async () => {
    setRefreshing(true);
    await loadData(true);
  };

  // ============================================
  // TENANT SELECTION
  // ============================================
  const handleSelectTenant = (tenantId) => {
    setSelectedTenants(prev => 
      prev.includes(tenantId) 
        ? prev.filter(id => id !== tenantId) 
        : [...prev, tenantId]
    );
  };

  const handleSelectAllTenants = (selectAll) => {
    if (selectAll) {
      const allTenantIds = tenantSubscriptions.map(t => t.tenantId);
      setSelectedTenants(allTenantIds);
    } else {
      setSelectedTenants([]);
    }
  };

  // ============================================
  // VIEW HISTORY
  // ============================================
  const handleViewHistory = (tenant) => {
    setHistoryTenant(tenant);
    setShowHistoryModal(true);
  };

  // ============================================
  // FORM HANDLERS
  // ============================================
  const handlePlanFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPlanForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAssignFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAssignForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // ============================================
  // PLAN CRUD
  // ============================================
  const handleCreatePlan = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const taxInput = planForm.tax || {};
      const normalizedTax = {
        isTaxInclusive: taxInput.isTaxInclusive ?? planForm.isTaxInclusive ?? false,
        taxName: taxInput.taxName || planForm.taxName || 'GST',
        taxPercentage: parseFloat(taxInput.taxPercentage ?? planForm.taxPercentage ?? 0) || 0
      };

      const basePayload = {
        name: planForm.name,
        description: planForm.description,
        type: planForm.type,
        pricing: {
          yearly: { amount: parseFloat(planForm.annualPrice) || 0, currency: 'INR', isActive: true }
        },
        tax: normalizedTax,
        features: {
          maxStudents: parseInt(planForm.maxStudents) || 0,
          maxStaff: parseInt(planForm.maxStaff) || 0,
          maxBranches: parseInt(planForm.maxBranches) || 1,
          maxAdmins: parseInt(planForm.maxAdmins) || 1,
          maxStorageGB: parseInt(planForm.maxStorageGB) || 5,
          maxCourses: 0,
          maxClasses: 0,
          maxExams: 0,
          maxFeeCategories: 0,
          maxInvoices: 0,
          maxReports: 0,
          maxAPIRequests: 1000,
          maxConcurrentUsers: 10
        },
        modules: {},
        roleAccess: {},
        support: {
          level: 'basic',
          responseTime: '24 hours',
          chatSupport: false,
          phoneSupport: false,
          dedicatedManager: false
        },
        trial: {
          enabled: planForm.trialEnabled,
          durationDays: parseInt(planForm.trialDays) || 14,
          featuresDuringTrial: 'limited',
          maxTrialsPerTenant: 1,
          limitedFeatures: {
            maxStudents: 5,
            maxStaff: 2,
            maxBranches: 1,
            maxAdmins: 1,
            maxStorageGB: 1,
            maxCourses: 3,
            maxClasses: 5,
            maxExams: 2
          },
          trialModules: {
            dashboard: true,
            settings: true,
            studentManagement: true,
            staffManagement: true,
            classliveview: true,
            websiteManagement: false,
            feeManagement: false,
            reports: false
          }
        },
        isActive: planForm.isActive,
        isPublic: planForm.isPublic,
        adminId: adminId
      };

      let res;

      if (planForm.isPublic) {
        res = await api.post('/api/super-admin/subscription/plans', basePayload);
      } else {
        res = await api.post('/api/super-admin/subscription/plans/private', {
          ...basePayload,
          assignedTenants: [],
          visibilityRestrictions: {},
          tenantAccessTiers: 'none'
        });
      }

      if (res.data.success) {
        success(planForm.isPublic ? 'Public plan created successfully!' : 'Private plan created successfully!');
        await loadData(true);
        setShowCreatePlan(false);
        setPlanForm({
          name: '',
          description: '',
          type: 'paid',
          annualPrice: '',
          taxName: 'GST',
          taxPercentage: '',
          isTaxInclusive: false,
          maxStudents: '',
          maxStaff: '',
          maxBranches: '1',
          maxAdmins: '1',
          maxStorageGB: '5',
          isActive: true,
          isPublic: true,
          trialEnabled: false,
          trialDays: '14'
        });
        setError(null);
      }
    } catch (err) {
      console.error('❌ Error creating plan:', err);
      const msg = err.response?.data?.message || 'Failed to create plan';
      
      if (err.response?.data?.code === 'DUPLICATE_PLAN_NAME') {
        showError(`A plan with the name "${planForm.name}" already exists. Please use a different name.`);
      } else {
        showError(msg);
      }
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

// ============================================
// PLAN CRUD - FIXED handleUpdatePlan
// ============================================
const handleUpdatePlan = async (formData) => {
  if (!selectedPlan) return;
  setIsSubmitting(true);
  
  try {
    // ✅ CRITICAL FIX: Use the trial data directly from the modal
    // The modal sends trial as an object in formData.trial
    // DO NOT recalculate - the modal already did it correctly
    
    console.log('📝 [handleUpdatePlan] Received formData:', formData);

    const taxInput = formData.tax || {};
    const normalizedTax = {
      isTaxInclusive: taxInput.isTaxInclusive ?? formData.isTaxInclusive ?? false,
      taxName: taxInput.taxName || formData.taxName || 'GST',
      taxPercentage: parseFloat(taxInput.taxPercentage ?? formData.taxPercentage ?? 0) || 0
    };
    
    // ✅ Use the trial data from the modal (already calculated correctly)
    const trialData = formData.trial || {
      enabled: false,
      durationDays: 0,
      featuresDuringTrial: 'limited',
      maxTrialsPerTenant: 1,
      limitedFeatures: {
        maxStudents: 5,
        maxStaff: 2,
        maxBranches: 1,
        maxAdmins: 1,
        maxStorageGB: 1,
        maxCourses: 3,
        maxClasses: 5,
        maxExams: 2
      },
      trialModules: {
        dashboard: true,
        settings: true,
        studentManagement: true,
        staffManagement: true,
        classliveview: true,
        websiteManagement: false,
        feeManagement: false,
        reports: false
      }
    };

    console.log('📝 [handleUpdatePlan] Using trial data from modal:', trialData);

    const payload = {
      name: formData.name,
      description: formData.description,
      type: formData.type,
      pricing: {
        yearly: { amount: parseFloat(formData.annualPrice) || 0, currency: 'INR', isActive: true }
      },
      tax: normalizedTax,
      features: {
        maxStudents: parseInt(formData.maxStudents) || 0,
        maxStaff: parseInt(formData.maxStaff) || 0,
        maxBranches: parseInt(formData.maxBranches) || 1,
        maxAdmins: parseInt(formData.maxAdmins) || 1,
        maxStorageGB: parseInt(formData.maxStorageGB) || 5,
        maxCourses: 0,
        maxClasses: 0,
        maxExams: 0,
        maxFeeCategories: 0,
        maxInvoices: 0,
        maxReports: 0,
        maxAPIRequests: 1000,
        maxConcurrentUsers: 10
      },
      modules: formData.modules || {},
      roleAccess: formData.roleAccess || {},
      support: {
        level: 'basic',
        responseTime: '24 hours',
        chatSupport: false,
        phoneSupport: false,
        dedicatedManager: false
      },
      // ✅ Use trialData directly from modal - NO RECALCULATION
      trial: trialData,
      isActive: formData.isActive,
      isPublic: formData.isPublic,
      visibility: formData.visibility || 'public',
      assignedTenants: formData.assignedTenants || [],
      invitedTenants: formData.invitedTenants || [],
      adminId: adminId
    };

    console.log('📝 [handleUpdatePlan] Sending payload trial:', payload.trial);
    console.log('📝 [handleUpdatePlan] Sending payload tax:', payload.tax);

    const res = await api.put(`/api/super-admin/subscription/plans/${selectedPlan.planId}`, payload);
    if (res.data.success) {
      success('Plan updated successfully!');
      await loadData(true);
      setShowEditPlan(false);
      setSelectedPlan(null);
      setError(null);
    }
  } catch (err) {
    console.error('❌ Error updating plan:', err);
    const msg = err.response?.data?.message || 'Failed to update plan';
    setError(msg);
    showError(msg);
  } finally {
    setIsSubmitting(false);
  }
};

  const handleDeletePlan = (planId) => {
    showConfirm(
      'Delete Plan',
      'Are you sure you want to delete this plan? This action cannot be undone.',
      async () => {
        try {
          const res = await api.delete(`/api/super-admin/subscription/plans/${planId}`, {
            data: { adminId }
          });
          if (res.data.success) {
            success('Plan deleted successfully!');
            await loadData(true);
            setError(null);
          }
        } catch (err) {
          console.error('❌ Error deleting plan:', err);
          const msg = err.response?.data?.message || 'Failed to delete plan';
          setError(msg);
          showError(msg);
        }
      },
      'Delete',
      'danger'
    );
  };

  // ============================================
  // ASSIGN SUBSCRIPTION (Legacy)
  // ============================================
  const handleAssignSubscription = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        tenantId: assignForm.tenantId,
        planId: assignForm.planId,
        status: assignForm.status,
        billingCycle: assignForm.billingCycle,
        isTrial: assignForm.isTrial,
        trialDays: parseInt(assignForm.trialDays) || 14,
        specialPrice: assignForm.specialPrice ? parseFloat(assignForm.specialPrice) : null,
        adminId: adminId
      };

      const res = await api.post('/api/super-admin/subscription/tenant/assign', payload);
      if (res.data.success) {
        success('Subscription assigned successfully!');
        await loadData(true);
        setShowAssignPlan(false);
        setAssignForm({
          tenantId: '',
          planId: '',
          status: 'active',
          billingCycle: 'yearly',
          isTrial: false,
          trialDays: '14',
          specialPrice: ''
        });
        setError(null);
      }
    } catch (err) {
      console.error('❌ Error assigning subscription:', err);
      const msg = err.response?.data?.message || 'Failed to assign subscription';
      setError(msg);
      showError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // TENANT SUBSCRIPTION ACTIONS
  // ============================================

  const handleSetSpecialPrice = async (tenantId, data) => {
    setIsSubmitting(true);
    try {
      const res = await api.post(`/api/super-admin/subscription/tenant/${tenantId}/special-price`, {
        ...data,
        adminId
      });
      if (res.data.success) {
        success(res.data.message);
        setShowSpecialPriceModal(false);
        setSelectedTenantForAction(null);
        setTimeout(() => {
          loadData(true);
        }, 500);
        setError(null);
      }
    } catch (err) {
      console.error('❌ Error setting special price:', err);
      const msg = err.response?.data?.message || 'Failed to set special price';
      setError(msg);
      showError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartTrial = async (tenantId, data) => {
    setIsSubmitting(true);
    try {
      const res = await api.post(`/api/super-admin/subscription/tenant/${tenantId}/trial`, {
        ...data,
        adminId
      });
      if (res.data.success) {
        success(res.data.message);
        await loadData(true);
        setShowTrialModal(false);
        setSelectedTenantForAction(null);
        setError(null);
      }
    } catch (err) {
      console.error('❌ Error starting trial:', err);
      const msg = err.response?.data?.message || 'Failed to start trial';
      setError(msg);
      showError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivateSubscription = async (tenantId, data) => {
    setIsSubmitting(true);
    try {
      const res = await api.post(`/api/super-admin/subscription/tenant/${tenantId}/activate`, {
        ...data,
        adminId
      });
      if (res.data.success) {
        success(res.data.message);
        await loadData(true);
        setShowManageModal(false);
        setSelectedTenant(null);
        setError(null);
      }
    } catch (err) {
      console.error('❌ Error activating subscription:', err);
      const msg = err.response?.data?.message || 'Failed to activate subscription';
      setError(msg);
      showError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivateSubscription = (tenantId) => {
    showConfirm(
      'Deactivate Subscription',
      'Are you sure you want to deactivate this subscription? The tenant will lose access immediately.',
      async () => {
        try {
          const res = await api.post(`/api/super-admin/subscription/tenant/${tenantId}/deactivate`, {
            reason: 'Deactivated by Super Admin',
            adminId
          });
          if (res.data.success) {
            success('Subscription deactivated successfully!');
            await loadData(true);
            setShowManageModal(false);
            setSelectedTenant(null);
            setError(null);
          }
        } catch (err) {
          console.error('❌ Error deactivating subscription:', err);
          const msg = err.response?.data?.message || 'Failed to deactivate subscription';
          setError(msg);
          showError(msg);
        }
      },
      'Deactivate',
      'danger'
    );
  };

  const handleExtendSubscription = async (tenantId, days, reason) => {
    setIsSubmitting(true);
    try {
      const res = await api.post(`/api/super-admin/subscription/tenant/${tenantId}/extend`, {
        days,
        reason: reason || 'Manual extension by Super Admin',
        adminId
      });
      if (res.data.success) {
        success(`Subscription extended by ${days} days successfully!`);
        await loadData(true);
        setShowManageModal(false);
        setSelectedTenant(null);
        setError(null);
      }
    } catch (err) {
      console.error('❌ Error extending subscription:', err);
      const msg = err.response?.data?.message || 'Failed to extend subscription';
      setError(msg);
      showError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManageTenantSubscription = async (tenantId, formData) => {
    setIsSubmitting(true);
    try {
      if (formData.action === 'activate') {
        await handleActivateSubscription(tenantId, formData);
      } else if (formData.action === 'trial') {
        await handleStartTrial(tenantId, formData);
      } else if (formData.action === 'deactivate') {
        await handleDeactivateSubscription(tenantId);
      } else if (formData.action === 'extend') {
        await handleExtendSubscription(tenantId, formData.days, formData.reason);
      } else {
        const payload = {
          planId: formData.planId,
          status: formData.status,
          billingCycle: formData.billingCycle,
          specialPrice: formData.specialPrice ? parseFloat(formData.specialPrice) : null,
          specialPriceReason: formData.customPriceReason || '',
          isTrial: formData.isTrial,
          trialDays: parseInt(formData.trialDays) || 14,
          actionType: formData.isTrial ? 'trial' : 'active',
          adminId
        };

        const res = await api.put(`/api/super-admin/subscription/tenant/${tenantId}`, payload);
        if (res.data.success) {
          const msg = res.data.isNew ? 'Subscription created successfully!' : 'Subscription updated successfully!';
          success(msg);
          await loadData(true);
          setShowManageModal(false);
          setSelectedTenant(null);
          setError(null);
        }
      }
    } catch (err) {
      console.error('❌ Error managing subscription:', err);
      const msg = err.response?.data?.message || 'Failed to manage subscription';
      setError(msg);
      showError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // FILTERS
  // ============================================
  const getFilteredSubscriptions = () => {
    let filtered = tenantSubscriptions;
    
    if (searchTerm) {
      filtered = filtered.filter(sub => 
        (sub.organizationName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.tenantId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.subdomain || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(sub => {
        const status = sub.subscriptionStatus || sub.status || 'none';
        return status === filterStatus;
      });
    }
    
    if (filterPlan !== 'all') {
      filtered = filtered.filter(sub => {
        const planId = sub.planId || sub.subscription?.planId;
        return planId === filterPlan;
      });
    }
    
    return filtered;
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderPlansTab = () => (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-900">Subscription Plans</h2>
          <span className="px-2.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full">
            {plans.length} plans
          </span>
          {plans.some(p => p.limits) && (
            <span className="px-2.5 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1">
              <Check className="w-3 h-3" /> Limits Synced
            </span>
          )}
        </div>
        <button
          onClick={() => setShowCreatePlan(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Create Plan
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {plans.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
          <Package className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No Plans Created</h3>
          <p className="text-sm text-slate-500 mt-1">Create your first subscription plan to get started</p>
          <button
            onClick={() => setShowCreatePlan(true)}
            className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors"
          >
            Create Plan
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <PlanCard 
              key={plan.planId || plan._id} 
              plan={plan}
              onEdit={() => {
                setSelectedPlan(plan);
                setShowEditPlan(true);
              }}
              onDelete={handleDeletePlan}
            />
          ))}
        </div>
      )}
    </div>
  );

  const renderTenantsTab = () => {
    const filteredSubs = getFilteredSubscriptions();
    
    return (
      <div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 w-full sm:w-auto min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search tenants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-64 pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
            <option value="suspended">Suspended</option>
            <option value="none">No Subscription</option>
          </select>

          <select
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
          >
            <option value="all">All Plans</option>
            {plans.filter(p => p.isActive).map(plan => (
              <option key={plan.planId} value={plan.planId}>
                {plan.name}
              </option>
            ))}
          </select>
          
          {selectedTenants.length > 0 && (
            <button
              onClick={() => setShowAssignTenantsModal(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              Assign {selectedTenants.length} Tenant(s)
            </button>
          )}
          
          <button
            onClick={refreshData}
            disabled={refreshing}
            className="px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing && 'animate-spin'}`} />
            Refresh
          </button>
        </div>

        <DashboardStats stats={stats} />

        <div className="mt-6">
          <SubscriptionTable
            subscriptions={filteredSubs}
            plans={plans}
            onManage={(tenant) => {
              setSelectedTenant(tenant);
              setShowManageModal(true);
            }}
            onViewHistory={handleViewHistory}
            onExtendQuick={(tenantId, days) => handleExtendSubscription(tenantId, days, 'Quick extend')}
            onSetTrial={(tenantId, days) => {
              setSelectedTenantForAction({ 
                tenantId, 
                days,
                status: tenantSubscriptions.find(t => t.tenantId === tenantId)?.subscriptionStatus || 'none'
              });
              setShowTrialModal(true);
            }}
            onSetSpecialPrice={(tenant) => {
              const fullTenant = tenantSubscriptions.find(t => t.tenantId === tenant.tenantId);
              setSelectedTenantForAction({
                ...tenant,
                subscriptionStatus: fullTenant?.subscriptionStatus || 'none',
                daysRemaining: fullTenant?.daysRemaining || 0,
                subscriptionId: fullTenant?.subscriptionId || null
              });
              setShowSpecialPriceModal(true);
            }}
            onDeactivate={handleDeactivateSubscription}
            selectedTenants={selectedTenants}
            onSelectTenant={handleSelectTenant}
            onSelectAll={handleSelectAllTenants}
            onRefresh={refreshData}
          />
        </div>
      </div>
    );
  };

  const renderStatsTab = () => (
    <div>
      <DashboardStats stats={stats} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Subscription Distribution</h3>
          {tenantSubscriptions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-slate-500">No subscription data available</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Active</span>
                  <span className="text-slate-900 font-medium">{stats.activeSubscriptions}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div 
                    className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((stats.activeSubscriptions / (tenantSubscriptions.length || 1)) * 100, 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Trial</span>
                  <span className="text-slate-900 font-medium">{stats.trialSubscriptions}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((stats.trialSubscriptions / (tenantSubscriptions.length || 1)) * 100, 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Expired</span>
                  <span className="text-slate-900 font-medium">{stats.expiredSubscriptions}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div 
                    className="bg-rose-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((stats.expiredSubscriptions / (tenantSubscriptions.length || 1)) * 100, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Conversion Rate</span>
              <span className="text-emerald-600 font-bold">{stats.conversionRate || 0}%</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button
              onClick={() => setShowCreatePlan(true)}
              className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-indigo-50 rounded-xl transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-indigo-100 rounded-lg">
                  <Plus className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Create New Plan</p>
                  <p className="text-xs text-slate-500">Add a new subscription plan</p>
                </div>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
            
            <button 
              onClick={() => setShowAssignPlan(true)}
              className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-emerald-50 rounded-xl transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-emerald-100 rounded-lg">
                  <Users className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Assign Plan to Tenant</p>
                  <p className="text-xs text-slate-500">Manually assign a subscription</p>
                </div>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
            
            <button 
              onClick={() => setActiveTab('tenants')}
              className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-blue-50 rounded-xl transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-blue-100 rounded-lg">
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Manage Tenants</p>
                  <p className="text-xs text-slate-500">View and manage all tenant subscriptions</p>
                </div>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="relative mx-auto w-16 h-16">
            <div className="w-16 h-16 border-4 border-indigo-200 rounded-full" />
            <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0" />
          </div>
          <p className="mt-4 text-slate-600 font-medium">Loading subscription data...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/super-admin/dashboard')}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Subscription Management</h1>
              <p className="text-sm text-slate-500">Manage plans, tenants, and subscriptions</p>
            </div>
          </div>
          <button
            onClick={refreshData}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing && 'animate-spin'}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 bg-white px-6">
        <div className="flex items-center gap-1 overflow-x-auto">
          <button
            onClick={() => setActiveTab('plans')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'plans' ? 'text-indigo-600 border-indigo-600' : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Package className="w-4 h-4" /> Plans
            </span>
          </button>
          <button
            onClick={() => setActiveTab('tenants')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'tenants' ? 'text-indigo-600 border-indigo-600' : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" /> Tenants
            </span>
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'stats' ? 'text-indigo-600 border-indigo-600' : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Stats
            </span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'plans' && renderPlansTab()}
        {activeTab === 'tenants' && renderTenantsTab()}
        {activeTab === 'stats' && renderStatsTab()}
      </div>

      {/* ============================================ */}
      {/* MODALS */}
      {/* ============================================ */}

      <CreatePlanModal 
        isOpen={showCreatePlan} 
        onClose={() => setShowCreatePlan(false)}
        formState={planForm}
        onChange={handlePlanFormChange}
        onSubmit={handleCreatePlan}
        isSubmitting={isSubmitting}
        error={error}
      />

      <EditPlanModal
        isOpen={showEditPlan}
        onClose={() => {
          setShowEditPlan(false);
          setSelectedPlan(null);
        }}
        plan={selectedPlan}
        onSave={handleUpdatePlan}
        isSaving={isSubmitting}
        modulesList={MODULE_CATEGORIES}
        rolesList={ROLES_LIST}
        error={error}
      />

      <AssignPlanModal 
        isOpen={showAssignPlan}
        onClose={() => setShowAssignPlan(false)}
        formState={assignForm}
        plans={plans}
        onChange={handleAssignFormChange}
        onSubmit={handleAssignSubscription}
        isSubmitting={isSubmitting}
      />

      <ManageTenantSubscriptionModal
        isOpen={showManageModal}
        onClose={() => {
          setShowManageModal(false);
          setSelectedTenant(null);
        }}
        tenant={selectedTenant}
        plans={plans}
        onSubmit={handleManageTenantSubscription}
        onExtend={handleExtendSubscription}
        onDeactivate={handleDeactivateSubscription}
        isSubmitting={isSubmitting}
        onRefresh={refreshData}
      />

      <AssignTenantsToPlanModal
        isOpen={showAssignTenantsModal}
        onClose={() => {
          setShowAssignTenantsModal(false);
          setSelectedTenants([]);
        }}
        selectedTenants={selectedTenants}
        plans={plans}
        onSuccess={refreshData}
      />

      <SubscriptionHistoryModal
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
          setHistoryTenant(null);
        }}
        tenantId={historyTenant?.tenantId}
        tenantName={historyTenant?.tenantName}
      />

      <SetSpecialPriceModal
        isOpen={showSpecialPriceModal}
        onClose={() => {
          setShowSpecialPriceModal(false);
          setSelectedTenantForAction(null);
        }}
        tenant={selectedTenantForAction}
        plans={plans}
        onSubmit={handleSetSpecialPrice}
        isSubmitting={isSubmitting}
      />

      <StartTrialModal
        isOpen={showTrialModal}
        onClose={() => {
          setShowTrialModal(false);
          setSelectedTenantForAction(null);
        }}
        tenant={selectedTenantForAction}
        plans={plans}
        onSubmit={handleStartTrial}
        isSubmitting={isSubmitting}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        type={confirmDialog.type}
      />
    </div>
  );
};

export default SubscriptionManagement;